import { apiClient } from './client';
import type {
  WhiteLabel,
  CreateWhiteLabelData,
  UpdateWhiteLabelData,
  WhiteLabelResponse,
  SyncResponse,
} from '@/types/white-label';

export async function getWhiteLabels(): Promise<WhiteLabel[]> {
  const response = await apiClient.get<WhiteLabel[]>('/api/white-labels');
  return response.data;
}

export async function getWhiteLabel(id: number): Promise<WhiteLabel> {
  const response = await apiClient.get<WhiteLabel>(`/api/white-labels/${id}`);
  return response.data;
}

export async function createWhiteLabel(data: CreateWhiteLabelData): Promise<WhiteLabelResponse> {
  const response = await apiClient.post<WhiteLabelResponse>('/api/white-labels', data);
  return response.data;
}

export async function updateWhiteLabel(
  id: number,
  data: UpdateWhiteLabelData
): Promise<WhiteLabelResponse> {
  const response = await apiClient.put<WhiteLabelResponse>(`/api/white-labels/${id}`, data);
  return response.data;
}

export async function deleteWhiteLabel(id: number): Promise<{ message?: string; error?: string }> {
  const response = await apiClient.delete<{ message?: string; error?: string }>(`/api/white-labels/${id}`);
  return response.data;
}

export async function syncWhiteLabels(): Promise<SyncResponse> {
  const response = await apiClient.post<SyncResponse>('/api/white-labels/sync');
  return response.data;
}

export interface PushBrandingResponse {
  pushed: boolean;
  reason?: string;
  message: string;
  settings?: Record<string, unknown>;
}

/**
 * Push the white label branding (images of its school) to the settings JSON
 * of its V2 tenant group
 */
export async function pushWhiteLabelBranding(id: number): Promise<PushBrandingResponse> {
  const response = await apiClient.post<PushBrandingResponse>(
    `/api/white-labels/${id}/push-branding`
  );
  return response.data;
}

// V2 Tenant Groups interface for deployment dialogs
export interface V2TenantGroupListResponse {
  data: Array<{
    id: string;
    name: string;
    host: string;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    tenantCount: number;
    createdAt: string | null;
    /** Réseau physique local portant ce tenant group (null = groupe par défaut) */
    networkId: number | null;
    isDefault: boolean;
  }>;
  defaultGroupId: string | null;
  warning?: string;
}

/**
 * Get all white labels including the default one (default group resolution)
 */
async function getWhiteLabelsWithDefault(): Promise<WhiteLabel[]> {
  const response = await apiClient.get<WhiteLabel[]>('/api/white-labels', {
    params: { includeDefault: 1 },
  });
  return response.data;
}

/**
 * Get V2 tenant groups for deployment selection.
 *
 * Les tenant groups sélectionnables sont dérivés des RÉSEAUX PHYSIQUES
 * (un réseau physique = un tenant group V2) + le groupe par défaut École
 * Futée (marque blanche isDefault, sans réseau).
 */
export async function getV2TenantGroups(): Promise<V2TenantGroupListResponse> {
  const { getSchoolNetworks } = await import('./school-networks');
  const [networks, whiteLabels] = await Promise.all([
    getSchoolNetworks('physical'),
    /**
     * Le groupe par défaut se lit dans les marques blanches, dont la liste
     * exige `action.white_labels.list` — permission absente du rôle Support,
     * qui a pourtant `action.school_networks.list`. Sans ce `catch`, le 403
     * faisait échouer tout l'appel : plus aucun réseau physique proposé au
     * déploiement (« Aucun résultat »), alors que les réseaux, eux, étaient
     * bien lisibles. Le groupe par défaut est optionnel : il n'entre pas dans
     * la requête de déploiement (le backend résout le tenant group lui-même).
     */
    getWhiteLabelsWithDefault().catch(() => [] as WhiteLabel[]),
  ]);

  const data: V2TenantGroupListResponse['data'] = networks
    .filter((n) => n.v2TenantGroupId)
    .map((n) => ({
      id: n.v2TenantGroupId!,
      name: n.name,
      host: n.v2Host || '',
      status: (n.v2Status || 'ACTIVE') as 'ACTIVE' | 'INACTIVE' | 'SUSPENDED',
      tenantCount: n.schoolCount,
      createdAt: n.createdAt,
      networkId: n.id,
      isDefault: false,
    }));

  // Groupe par défaut (École Futée) : jamais porté par un réseau
  const defaultWhiteLabel = whiteLabels.find((wl) => wl.isDefault);
  const defaultGroupId = defaultWhiteLabel?.v2TenantGroupId || null;
  if (defaultGroupId) {
    data.unshift({
      id: defaultGroupId,
      name: defaultWhiteLabel!.name,
      host: defaultWhiteLabel!.v2Host || '',
      status: (defaultWhiteLabel!.v2Status || 'ACTIVE') as 'ACTIVE' | 'INACTIVE' | 'SUSPENDED',
      tenantCount: defaultWhiteLabel!.tenantCount || 0,
      createdAt: defaultWhiteLabel!.createdAt,
      networkId: null,
      isDefault: true,
    });
  }

  return {
    data,
    defaultGroupId,
  };
}
